import { useState, useCallback, useEffect } from 'react';
import { CalculatorConfig } from './types';

export function getDefaultValueForInput(input: any): any {
  if (input.defaultValue !== undefined
    && input.defaultValue !== null
    && (input.defaultValue !== '' && input.type !== "number" && input.type !== "boolean")) {
    return input.defaultValue;
  }
  if (input.type === 'number') {
    return input.defaultValue !== undefined ? input.defaultValue : 0;
  }
  if (input.type === 'boolean') {
    return false;
  }
  if (input.type === 'select') {
    return input.options?.[0]?.value ?? '';
  }
  if (input.type === 'separator') {
    return undefined;
  }
  return input.defaultValue ?? '';
}

export function getDefaultCalculatorValues(
  cfg?: Partial<CalculatorConfig> | null,
  init?: Record<string, any>
): Record<string, any> {
  const result: Record<string, any> = { ...(init || {}) };
  if (!cfg?.inputs) return result;

  (cfg.inputs || []).forEach((input) => {
    if (result[input.name] === undefined) {
      result[input.name] = getDefaultValueForInput(input);
    }
  });

  return result;
}

export function useCalculatorRunnerState(
  config: CalculatorConfig,
  initialValues?: Record<string, any>
) {
  const [inputValues, setInputValues] = useState<Record<string, any>>(() =>
    getDefaultCalculatorValues(config, initialValues)
  );

  // Sync missing input default values when config inputs are updated or added
  useEffect(() => {
    setInputValues((prev) => {
      let changed = false;
      const next = { ...prev };
      (config.inputs || []).forEach((input) => {
        if (next[input.name] === undefined) {
          next[input.name] = getDefaultValueForInput(input);
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [config.inputs]);

  const updateInputValues = useCallback(
    (
      updater:
        | Record<string, any>
        | ((prev: Record<string, any>) => Record<string, any>)
        | string,
      fieldValue?: any
    ) => {
      if (typeof updater === 'string') {
        setInputValues((prev) => ({ ...prev, [updater]: fieldValue }));
      } else if (typeof updater === 'function') {
        setInputValues(updater);
      } else {
        setInputValues(updater);
      }
    },
    []
  );

  return {
    inputValues,
    updateInputValues,
    setInputValues,
  };
}

export default useCalculatorRunnerState;
